import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import PhoneForm, { type PhoneReason } from "@/components/Forms/PhoneForm";
import { ProviderOnboardingStep } from "../enums/ProviderOnboardingStep";
import { useEffect } from "react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CalendarRange,
  Settings,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { ProviderUser } from "@/common/types/ProviderUser";

// Copy for org-affiliated (roster) providers: their texts are about credentials,
// job scheduling, and getting work/payments — never marketplace job searching.
const ORG_MEMBER_PHONE_REASONS: PhoneReason[] = [
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    text: "Reminders when your credentials need attention or renewal",
  },
  {
    icon: <CalendarRange className="h-5 w-5" />,
    text: "Updates about your job scheduling and upcoming shifts",
  },
  {
    icon: <Wallet className="h-5 w-5" />,
    text: "Important messages about getting work and receiving your payments",
  },
  {
    icon: <Settings className="h-5 w-5" />,
    text: "We never send spam — you can adjust your message settings anytime",
  },
];

export default function TextNotificationsOnboarding({
  onComplete,
  onBack,
  providerUser,
}: {
  onComplete: () => void;
  onBack?: () => void;
  providerUser: ProviderUser;
}) {
  const trpc = useTRPC();
  const { mutateAsync: updateOnboardingStep } = useMutation(
    trpc.providerOnboarding.updateProviderOnboardingStep.mutationOptions(),
  );
  const { data: invitationPhone } = useSuspenseQuery(
    trpc.providerOnboarding.getInvitationPhonePrefill.queryOptions(),
  );

  const handleNext = async () => {
    await updateOnboardingStep({
      onboardingStepId: ProviderOnboardingStep.TEXT_NOTIFICATIONS,
    });
    onComplete();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      {onBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={ONBOARDING_FORM_STYLES.backButton}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back
        </Button>
      )}
      <PhoneForm
        onComplete={handleNext}
        defaultPhoneNumber={providerUser.phone ?? invitationPhone.phone ?? ""}
        reasons={
          providerUser.jobPostsEnabled ? undefined : ORG_MEMBER_PHONE_REASONS
        }
        confirmSkip={!providerUser.jobPostsEnabled}
      />
    </div>
  );
}
